// Derives extraction-quality issues from a run's stats so the data-pipeline
// grid can flag suspicious results without opening the stats popup. Each issue
// carries a stable `category` (used by the grid's set filter) and a `detail`
// with the file-specific message (shown in tooltips). The run's own `error` is
// excluded — RunCell already shows it.

import {
  CaptableRunSummary,
  FinancialRunSummary,
} from "../../api/dataPipeline";

export interface RunIssue {
  category: string;
  detail: string;
}

function baseName(path: string): string {
  return path.split("/").pop() ?? path;
}

export function getCaptableRunIssues(
  run: CaptableRunSummary | null,
): RunIssue[] {
  const stats = run?.extractionStats;
  if (!stats) return [];
  const issues: RunIssue[] = [];

  if (!stats.mgt7) {
    issues.push({
      category: "MGT-7: no stats",
      detail: "MGT-7: no extraction stats",
    });
  } else {
    if (stats.mgt7.shareholders === 0) {
      issues.push({
        category: "MGT-7: 0 shareholders",
        detail: "MGT-7: 0 shareholders extracted",
      });
    }
    if (!stats.mgt7.providesIndividualList) {
      issues.push({
        category: "MGT-7: no individual list",
        detail: "MGT-7: no individual shareholder list",
      });
    }
  }

  stats.pas3s.forEach((p) => {
    const file = baseName(p.file);
    if (p.rounds === 0) {
      issues.push({
        category: "PAS-3: 0 rounds",
        detail: `${file}: 0 rounds extracted`,
      });
    }
    if (p.placeholders > 0) {
      issues.push({
        category: "PAS-3: placeholder allottees",
        detail: `${file}: ${p.placeholders} placeholder allottee${
          p.placeholders === 1 ? "" : "s"
        }`,
      });
    }
    p.loaIssues.forEach((issue) =>
      issues.push({
        category: "PAS-3: LOA issue",
        detail: `${file}: ${issue}`,
      }),
    );
  });

  if (stats.stage3Rows === 0) {
    issues.push({
      category: "No stage-3 rows",
      detail: "0 stage-3 rows generated",
    });
  }
  if (stats.allotmentRows === 0) {
    issues.push({
      category: "No allotment rows",
      detail: "0 allotment rows generated",
    });
  }

  return issues;
}

export function getFinancialRunIssues(
  run: FinancialRunSummary | null,
): RunIssue[] {
  const stats = run?.extractionStats;
  if (!stats) return [];
  const issues: RunIssue[] = [];

  stats.files.forEach((f) => {
    const label = f.financialYear || baseName(f.file);
    if (f.error) {
      issues.push({
        category: "AOC-4: extraction error",
        detail: `${label}: ${f.error}`,
      });
      return;
    }
    if (!f.hasBalanceSheet) {
      issues.push({
        category: "AOC-4: missing balance sheet",
        detail: `${label}: missing balance sheet`,
      });
    }
    if (!f.hasProfitAndLoss) {
      issues.push({
        category: "AOC-4: missing P&L",
        detail: `${label}: missing P&L`,
      });
    }
    if (f.nullFields > 0) {
      issues.push({
        category: "AOC-4: null fields",
        detail: `${label}: ${f.nullFields} null field${
          f.nullFields === 1 ? "" : "s"
        }`,
      });
    }
  });

  return issues;
}
